import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, Clock, BookOpen, Lock, AlertCircle } from "lucide-react";
import { TREES_BIRDS_URL } from "@/components/TreeMark";

const CHAPTER_HEADER = TREES_BIRDS_URL;

export default function Guide() {
  const { track } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setErr(""); setData(null);
    api.get(`/guide/${track}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (cancelled) return;
        console.error("Guide load failed:", e);
        const msg = e?.response
          ? `Server responded ${e.response.status}. Please try again in a moment.`
          : "We couldn't reach the server. Please check your connection and try again.";
        setErr(msg);
      });
    return () => { cancelled = true; };
  }, [track, attempt]);

  if (err) return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center">
      <div className="w-14 h-14 rounded-full bg-[#F5ECE7] flex items-center justify-center text-[#D1A793] mx-auto mb-6">
        <AlertCircle size={26} strokeWidth={1.5} />
      </div>
      <h2 className="font-serif text-2xl md:text-3xl text-[#2D332D] mb-3">We couldn't load this section.</h2>
      <p className="text-[#576056] mb-8 leading-relaxed">{err}</p>
      <div className="flex gap-3 justify-center flex-wrap">
        <button onClick={() => setAttempt((a) => a + 1)} data-testid="guide-retry" className="btn-primary">
          Try again
        </button>
        <button onClick={() => window.location.reload()} data-testid="guide-hard-reload" className="btn-secondary">
          Reload the page
        </button>
      </div>
      <p className="mt-8 text-xs text-[#8A9488] leading-relaxed">
        If this keeps happening, try a hard refresh: Ctrl + Shift + R (Windows) or Cmd + Shift + R (Mac).
      </p>
    </div>
  );
  if (!data) return <div className="max-w-3xl mx-auto px-6 py-24 text-[#8A9488]">Loading…</div>;

  return (
    <div>
      {/* Banner strip — trees & birds illustration */}
      <div
        aria-hidden
        className="w-full banner-strip"
        style={{
          backgroundImage: `url(${CHAPTER_HEADER})`,
        }}
      />

      {/* Header */}
      <section className="relative bg-[#FAF9F6]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 pt-14 pb-10 md:pt-20 md:pb-14">
          <div className="text-xs uppercase tracking-[0.2em] text-[#6B7F62] mb-4">
            {track === "bereaved" ? "Path one · If you've been bereaved" : "Path two · Preparing for your family"}
          </div>
          <h1 data-testid="guide-title" className="font-serif text-4xl md:text-5xl text-[#2D332D] leading-[1.05] mb-4">
            {data.title}
          </h1>
          <p className="text-lg text-[#576056] max-w-2xl leading-relaxed">{data.subtitle}</p>
        </div>
      </section>

      {/* Intro */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 mb-10">
        <div className="card p-8 border-l-4 border-l-[#9EAD94]">
          <p className="text-[#2D332D] text-[17px] leading-relaxed">{data.intro}</p>
        </div>
      </section>

      {/* Chapter list */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 pb-24">
        <div className="text-xs uppercase tracking-[0.15em] text-[#8A9488] mb-6 flex items-center gap-2">
          <BookOpen size={14} /> Chapters
        </div>
        <ol className="space-y-3" data-testid="chapter-list">
          {data.chapters.map((ch, i) => {
            const locked = !!ch.is_premium && ch.locked !== false;
            const isLockedForMe = !!ch.locked;
            return (
              <li key={ch.id}>
                <Link
                  to={`/guide/${track}/${ch.id}`}
                  data-testid={`chapter-link-${ch.id}`}
                  className={`card block px-6 py-5 md:px-8 md:py-6 transition-colors group ${
                    isLockedForMe ? "hover:border-[#D1A793]" : "hover:border-[#9EAD94]"
                  }`}
                >
                  <div className="flex items-start gap-5">
                    <div className={`font-serif text-3xl w-10 flex-shrink-0 leading-none pt-0.5 ${
                      isLockedForMe ? "text-[#D1A793]" : "text-[#9EAD94]"
                    }`}>
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-serif text-2xl text-[#2D332D] leading-snug">{ch.title}</h3>
                        {ch.is_premium && (
                          <span
                            data-testid={`chapter-premium-badge-${ch.id}`}
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] bg-[#2D332D] text-[#F5ECE7] px-2 py-0.5 rounded-full"
                          >
                            <Lock size={10} /> Premium
                          </span>
                        )}
                      </div>
                      <p className="text-[#576056] leading-relaxed text-[15px]">{ch.summary}</p>
                      <div className="mt-3 flex flex-col gap-1 text-xs text-[#8A9488]">
                        {!ch.is_premium && (
                          <span className="inline-flex items-center gap-1"><Clock size={12} /> {ch.reading_time}</span>
                        )}
                        {ch.is_premium && (
                          <>
                            <span className="text-[#D1A793]">Unlock all premium features with one-off payment of £29 for Lifetime Access</span>
                            <span className="text-[#D1A793]">Your key information is stored in your secure member area, ready for you to update whenever you need to</span>
                          </>
                        )}
                      </div>
                    </div>
                    <ArrowRight className="text-[#8A9488] group-hover:text-[#6B7F62] group-hover:translate-x-1 transition-all mt-2" size={18} />
                  </div>
                </Link>
              </li>
            );
          })}
        </ol>
      </section>
    </div>
  );
}
